import { heroPhotos } from '@/lib'
import { TemplatePage, TemplateSearch } from './TemplateChrome'
import type { TemplateProps } from './types'

const sections = [
  { title: 'Services and programs', text: 'What we offer, who it serves, and how to take part.', to: 'service-landing' as const },
  { title: 'Data and research', text: 'Reports, datasets and the work behind our decisions.', to: 'search-results' as const },
  { title: 'News and events', text: 'Announcements, statements and what is coming up.', to: null },
  { title: 'Contact us', text: 'Offices, phone numbers and the right place for each question.', to: null },
]

const news = [
  { date: 'October 14', title: 'Strategic plan for the next four years published', text: 'Our goals, and how we will measure progress.' },
  { date: 'September 30', title: 'Public comment period opens', text: 'Share your views on proposed changes through November.' },
  { date: 'September 12', title: 'Annual report to Congress', text: 'A year of work, by the numbers and in plain language.' },
]

/** An agency's front door: who it is and what it does, the main sections of its site, and its latest news. */
export default function AgencyHome({ mission, link, credit }: TemplateProps) {
  const photo = mission.media?.photos?.[0] ?? heroPhotos[0]
  return (
    <TemplatePage mission={mission} link={link} credit={credit}>
      <section className="mds-page-hero mds-page-hero--media" aria-labelledby="home-title">
        <img className="mds-page-hero__media" src={photo.small} srcSet={`${photo.small} 900w, ${photo.file} 2000w`} sizes="100vw" alt="" />
        <span className="mds-page-hero__shade" aria-hidden="true" />
        <div className="mds-container mds-stack mds-page-hero__body">
          <p className="mds-eyebrow">{mission.short}</p>
          <h1 id="home-title" className="mds-display">
            {mission.name}
          </h1>
          <p className="mds-lead">{mission.mission.text}</p>
          <TemplateSearch id="home-search" action={link('search-results')} />
        </div>
        <p className="mds-page-hero__credit">
          Photograph:{' '}
          <a href={photo.source}>
            {photo.title}, {photo.creator}
          </a>
        </p>
      </section>
      <section className="mds-section" aria-labelledby="sections-title">
        <div className="mds-container mds-stack">
          <h2 id="sections-title" className="mds-h2">
            Explore
          </h2>
          <ul className="mds-page-grid">
            {sections.map((section) => (
              <li key={section.title}>
                <article className="mds-card mds-card--outline mds-card--link">
                  <h3 className="mds-card__title">
                    <a className="mds-card__link" href={section.to ? link(section.to) : '#news'}>
                      {section.title}
                    </a>
                  </h3>
                  <p>{section.text}</p>
                </article>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section id="news" className="mds-section" aria-labelledby="news-title">
        <div className="mds-container mds-stack">
          <h2 id="news-title" className="mds-h2">
            News
          </h2>
          <ul className="mds-page-grid mds-page-grid--three">
            {news.map((item) => (
              <li key={item.title}>
                <article className="mds-card mds-card--raised mds-card--link">
                  <p className="mds-hint">{item.date}</p>
                  <h3 className="mds-card__title">
                    <a className="mds-card__link" href="#news">
                      {item.title}
                    </a>
                  </h3>
                  <p>{item.text}</p>
                </article>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </TemplatePage>
  )
}
